import React, { useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
    Form,
    Group,
    Input,
    Heading,
    LoadingIcon,
} from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";
import { isJson } from "../util";

import { CodeFormatter } from "components/CodeFormatter";
import { CopyButton } from "components/CopyButton";

const schema = yup.object().shape({
    userEmail: yup.string().required("User Email is required"),
    paymentType: yup.string().required("Payment Type is required"),
    currencyCode: yup.string().required("Currency Code is required"),
    amount: yup.string().required("Amount is required"),
    authCode: yup.string().required("Auth Code is required"),
});

export const ImportPaymentForm = () => {
    const config = useConfig();
    const API_URL = config.get("api");

    const [loading, setLoading] = useState(false);
    const [requestData, setRequestData] = useState("");
    const [responseData, setResponseData] = useState("");

    const onSubmitForm = async (formData, actions) => {
        setLoading(true);
        setResponseData("");

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        };

        try {
            const { data } = await axios.post(
                `${API_URL}/test-harness/import-payment`,
                {
                    userEmail: formData.userEmail,
                    paymentType: formData.paymentType,
                    currencyCode: formData.currencyCode,
                    amount: formData.amount,
                    authCode: formData.authCode,
                },
                {
                    headers,
                }
            );

            setRequestData(data?.request);
            setResponseData(data?.response);
        } catch (e) {
            const errorMessage = isJson(e?.response?.data?.["hydra:description"]) ? JSON.parse(e?.response?.data?.["hydra:description"])?.error?.message : e?.response?.data?.["hydra:description"] || e?.message

            toast.error(errorMessage);
        } finally {
            setLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Import Payment | Test Harnesses | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name="Import Payment" />
                <Form
                    autoComplete="on"
                    values={{
                        userEmail: "",
                        paymentType: "",
                        currencyCode: "",
                        amount: "",
                        authCode: "",
                    }}
                    schema={schema}
                    showActionPanel={false}
                    onSubmit={onSubmitForm}
                >
                    <Group
                        key="request-payload"
                        id="request-payload"
                        name="Request Payload"
                    >
                        <Input name="userEmail" label="User Email" testId="user-email" required help="The User email e.g. test@test.com" />
                        <Input name="paymentType" label="Payment Type" testId="payment-type" required help="The Payment Type e.g. Credit Card NP" />
                        <Input name="currencyCode" label="Currency Code" testId="currency-code" required help="The Currency Code e.g. GBP" />
                        <Input name="amount" label="Amount" testId="amount" required help="The Amount e.g. 100.83" type="number" />
                        <Input name="authCode" label="Auth Code" testId="auth-code" required help="The Auth Code e.g. 123456" />

                        <button
                            className="btn btn-primary"
                            type="submit"
                            data-testid="submit"
                        >
                            Submit
                        </button>
                    </Group>


                    {loading && (
                        <div className="flex items-center justify-center py-5">
                            <LoadingIcon />
                        </div>
                    )}

                    {requestData && !loading && (
                        <Group
                            key="request-details"
                            id="request-details"
                            name="Request Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    DC Import Payment Request
                                </h4>{" "}
                                <CopyButton content={JSON.stringify(requestData)} />
                            </div>
                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={JSON.stringify(requestData)}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}

                    {responseData && !loading && (
                        <Group
                            key="response-details"
                            id="response-details"
                            name="Response Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    DC Import Payment Response
                                </h4>{" "}
                                <CopyButton content={JSON.stringify(responseData)} />
                            </div>
                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={JSON.stringify(responseData)}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}
                </Form>
            </div>
        </>
    );
};

ImportPaymentForm.displayName = "ImportPaymentForm";